'use client';
import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';

interface WalletOption {
  id: string;
  name: string;
  desc: string;
  emoji: string;
  color: string;
  bg: string;
  network: string;
  isInstalled: boolean;
  installUrl: string;
}

function truncateAddress(addr: string): string {
  if (addr.length <= 12) return addr;
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

declare global {
  interface Window {
    ethereum?: any;
    solana?: any;
    trustwallet?: any;
  }
}

function detectWallets(): { metamask: boolean; phantom: boolean; trust: boolean } {
  if (typeof window === 'undefined') return { metamask: false, phantom: false, trust: false };
  const eth = window.ethereum;
  const metamask = !!(eth && (eth.isMetaMask || (eth.providers && eth.providers.some((p: any) => p.isMetaMask))));
  const phantom = !!(window.solana && window.solana.isPhantom);
  // Trust Wallet injects as window.trustwallet or window.ethereum with isTrust
  const trust = !!(window.trustwallet || (eth && eth.isTrust));
  return { metamask, phantom, trust };
}

async function connectMetaMask(): Promise<string> {
  const eth = window.ethereum;
  if (!eth) throw new Error('MetaMask not installed');
  // If multiple providers, find MetaMask
  let provider = eth;
  if (eth.providers) {
    provider = eth.providers.find((p: any) => p.isMetaMask) || eth;
  }
  const accounts: string[] = await provider.request({ method: 'eth_requestAccounts' });
  if (!accounts || accounts.length === 0) throw new Error('No accounts returned from MetaMask');
  return accounts[0];
}

async function connectPhantom(): Promise<string> {
  const solana = window.solana;
  if (!solana || !solana.isPhantom) throw new Error('Phantom not installed');
  const response = await solana.connect();
  return response.publicKey.toString();
}

async function connectTrustWallet(): Promise<string> {
  const tw = window.trustwallet;
  const eth = window.ethereum;
  if (tw) {
    const accounts: string[] = await tw.request({ method: 'eth_requestAccounts' });
    if (!accounts || accounts.length === 0) throw new Error('No accounts returned from Trust Wallet');
    return accounts[0];
  } else if (eth && eth.isTrust) {
    const accounts: string[] = await eth.request({ method: 'eth_requestAccounts' });
    if (!accounts || accounts.length === 0) throw new Error('No accounts returned from Trust Wallet');
    return accounts[0];
  }
  throw new Error('Trust Wallet not installed');
}

export default function WalletConnectTab() {
  const [connecting, setConnecting] = useState<string | null>(null);
  const [connectedWallet, setConnectedWallet] = useState<{ id: string; address: string; network: string } | null>(null);
  const [installed, setInstalled] = useState({ metamask: false, phantom: false, trust: false });
  const { user, connectWallet } = useAuth();
  const router = useRouter();

  useEffect(() => {
    // Detect installed wallets after mount (browser-only)
    setInstalled(detectWallets());
  }, []);

  const wallets: WalletOption[] = [
    {
      id: 'wallet-metamask',
      name: 'MetaMask',
      desc: installed.metamask ? 'Connect using your MetaMask browser extension' : 'MetaMask not detected — click to install',
      emoji: '🦊',
      color: '#F6851B',
      bg: 'rgba(246,133,27,0.1)',
      network: 'ETH',
      isInstalled: installed.metamask,
      installUrl: 'https://metamask.io/download/',
    },
    {
      id: 'wallet-phantom',
      name: 'Phantom',
      desc: installed.phantom ? 'Solana & multi-chain wallet by Phantom' : 'Phantom not detected — click to install',
      emoji: '👻',
      color: '#AB9FF2',
      bg: 'rgba(171,159,242,0.1)',
      network: 'SOL',
      isInstalled: installed.phantom,
      installUrl: 'https://phantom.app/',
    },
    {
      id: 'wallet-trust',
      name: 'Trust Wallet',
      desc: installed.trust ? 'Mobile-first multi-chain crypto wallet' : 'Trust Wallet not detected — click to install',
      emoji: '🛡️',
      color: '#3375BB',
      bg: 'rgba(51,117,187,0.1)',
      network: 'ETH',
      isInstalled: installed.trust,
      installUrl: 'https://trustwallet.com/download',
    },
  ];

  const handleConnect = async (wallet: WalletOption) => {
    if (!wallet.isInstalled) {
      window.open(wallet.installUrl, '_blank');
      toast.info(`${wallet.name} is not installed. Opening install page...`);
      return;
    }

    setConnecting(wallet.id);
    try {
      let address = '';

      if (wallet.id === 'wallet-metamask') {
        address = await connectMetaMask();
      } else if (wallet.id === 'wallet-phantom') {
        address = await connectPhantom();
      } else if (wallet.id === 'wallet-trust') {
        address = await connectTrustWallet();
      }

      if (!address) throw new Error('Could not retrieve wallet address');

      if (user) {
        // User is signed in — save wallet to Supabase
        await connectWallet(address, wallet.name, wallet.network);
        toast.success(`${wallet.name} connected and saved to your account!`);
      } else {
        toast.success(`${wallet.name} connected! Sign in or create an account to save it.`);
      }

      setConnectedWallet({ id: wallet.id, address, network: wallet.network });
    } catch (err: any) {
      if (err?.code === 4001) {
        toast.error('Connection rejected. Please approve the request in your wallet.');
      } else {
        toast.error(err?.message || `Failed to connect ${wallet.name}. Please try again.`);
      }
    } finally {
      setConnecting(null);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm mb-4" style={{ color: 'hsl(var(--text-secondary))' }}>
        Connect your crypto wallet to start completing tasks and earning rewards.
      </p>

      {wallets.map((wallet) => {
        const isConnecting = connecting === wallet.id;
        const isConnected = connectedWallet?.id === wallet.id;
        return (
          <button
            key={wallet.id}
            onClick={() => !isConnected && handleConnect(wallet)}
            disabled={isConnecting}
            className="w-full flex items-center gap-4 p-4 rounded-xl border transition-all duration-150 hover:scale-[1.02] active:scale-[0.98] hover:shadow-md disabled:opacity-60"
            style={{
              background: isConnected ? `${wallet.color}15` : 'hsl(var(--surface-2))',
              borderColor: isConnected ? wallet.color : 'hsl(var(--border))',
            }}
          >
            <span className="text-2xl">{wallet.emoji}</span>
            <div className="flex-1 text-left">
              <div className="flex items-center gap-2">
                <p className="font-semibold text-sm" style={{ color: 'hsl(var(--text))' }}>
                  {wallet.name}
                </p>
                {!wallet.isInstalled && (
                  <span
                    className="text-xs px-1.5 py-0.5 rounded-full font-medium"
                    style={{ background: 'rgba(245,200,66,0.15)', color: '#b8860b' }}
                  >
                    Not installed
                  </span>
                )}
              </div>
              <p className="text-xs" style={{ color: 'hsl(var(--text-secondary))' }}>
                {wallet.desc}
              </p>
            </div>
            <div className="flex-shrink-0">
              {isConnecting ? (
                <Icon name="ArrowPathIcon" size={18} className="animate-spin" style={{ color: wallet.color } as React.CSSProperties} />
              ) : isConnected ? (
                <div className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: 'hsl(var(--success))' }}>
                  <Icon name="CheckCircleIcon" size={16} />
                  Connected
                </div>
              ) : wallet.isInstalled ? (
                <Icon name="ChevronRightIcon" size={18} style={{ color: 'hsl(var(--muted))' } as React.CSSProperties} />
              ) : (
                <Icon name="ArrowTopRightOnSquareIcon" size={16} style={{ color: 'hsl(var(--muted))' } as React.CSSProperties} />
              )}
            </div>
          </button>
        );
      })}

      {connectedWallet && (
        <div
          className="mt-4 p-4 rounded-xl border animate-fade-in"
          style={{ background: 'hsl(var(--success) / 0.08)', borderColor: 'hsl(var(--success) / 0.3)' }}
        >
          <p className="text-sm font-semibold mb-1" style={{ color: 'hsl(var(--success))' }}>
            Wallet Connected
          </p>
          <p className="font-mono text-xs" style={{ color: 'hsl(var(--text-secondary))' }}>
            {truncateAddress(connectedWallet.address)} — {connectedWallet.network} Network
          </p>
          <button
            onClick={() => router.push('/dashboard')}
            className="mt-3 flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm font-bold transition-all duration-150 hover:scale-[1.02] active:scale-[0.98] hover:shadow-md"
            style={{ background: 'hsl(var(--primary))', color: '#fff' }}
          >
            Continue to Dashboard
            <Icon name="ArrowRightIcon" size={14} />
          </button>
        </div>
      )}

      <p className="text-xs text-center mt-2" style={{ color: 'hsl(var(--muted))' }}>
        Wallets not installed will open their official download page.
      </p>
    </div>
  );
}